// src/components/clients/ClientEquipment.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Reply as ReturnIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

const AssignEquipmentDialog = ({ open, onClose, onSubmit, items, warehouses }) => {
    const [formData, setFormData] = useState({
        itemId: '',
        warehouseId: '',
        serialNumber: '',
        macAddress: '',
        notes: ''
    });
    const [selectedItem, setSelectedItem] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setFormData({
                itemId: '',
                warehouseId: '',
                serialNumber: '',
                macAddress: '',
                notes: ''
            });
            setError('');
        }
    }, [open]);

    useEffect(() => {
        if (formData.itemId) {
            const item = items.find(i => i.id === formData.itemId);
            setSelectedItem(item);
        } else {
            setSelectedItem(null);
        }
    }, [formData.itemId, items]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        // Validación básica
        if (!formData.itemId || !formData.warehouseId) {
            setError('Por favor seleccione un producto y un almacén');
            return;
        }

        onSubmit(formData);
    };

    // Filtrar items con stock disponible por almacén
    const getAvailableItems = () => {
        if (!formData.warehouseId) return [];

        return items.filter(item => {
            const stock = item.stock?.find(s => s.warehouse_id === formData.warehouseId);
            return stock && stock.quantity > 0;
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Asignar Equipo al Cliente</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Almacén</InputLabel>
                                <Select
                                    name="warehouseId"
                                    value={formData.warehouseId}
                                    onChange={handleChange}
                                    label="Almacén"
                                >
                                    <MenuItem value="">Seleccione almacén</MenuItem>
                                    {warehouses.map(warehouse => (
                                        <MenuItem key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={!formData.warehouseId}>
                                <InputLabel>Producto</InputLabel>
                                <Select
                                    name="itemId"
                                    value={formData.itemId}
                                    onChange={handleChange}
                                    label="Producto"
                                >
                                    <MenuItem value="">Seleccione producto</MenuItem>
                                    {getAvailableItems().map(item => {
                                        const stock = item.stock?.find(s => s.warehouse_id === formData.warehouseId);
                                        return (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name} - {item.model} ({stock?.quantity} disponibles)
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>

                        {selectedItem && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Número de Serie"
                                        name="serialNumber"
                                        value={formData.serialNumber}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Dirección MAC"
                                        name="macAddress"
                                        value={formData.macAddress}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Notas"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        placeholder="Información adicional sobre el equipo, condición, configuración, etc."
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={!formData.itemId || !formData.warehouseId}
                >
                    Asignar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ReturnEquipmentDialog = ({ open, onClose, onSubmit, equipment, warehouses }) => {
    const [formData, setFormData] = useState({
        warehouseId: '',
        condition: 'good',
        notes: ''
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setFormData({
                warehouseId: '',
                condition: 'good',
                notes: ''
            });
            setError('');
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        if (!formData.warehouseId) {
            setError('Por favor seleccione un almacén');
            return;
        }

        onSubmit(equipment.id, formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Devolver Equipo</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <Typography sx={{ mb: 2 }}>
                        <strong>Producto:</strong> {equipment?.item_name} {equipment?.model}
                    </Typography>

                    {equipment?.serial_number && (
                        <Typography sx={{ mb: 2 }}>
                            <strong>Número de Serie:</strong> {equipment.serial_number}
                        </Typography>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Almacén de devolución</InputLabel>
                                <Select
                                    name="warehouseId"
                                    value={formData.warehouseId}
                                    onChange={handleChange}
                                    label="Almacén de devolución"
                                >
                                    <MenuItem value="">Seleccione almacén</MenuItem>
                                    {warehouses.map(warehouse => (
                                        <MenuItem key={warehouse.id} value={warehouse.id}>
                                            {warehouse.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Condición</InputLabel>
                                <Select
                                    name="condition"
                                    value={formData.condition}
                                    onChange={handleChange}
                                    label="Condición"
                                >
                                    <MenuItem value="good">Bueno</MenuItem>
                                    <MenuItem value="damaged">Dañado</MenuItem>
                                    <MenuItem value="defective">Defectuoso</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notas"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Observaciones sobre el estado del equipo, daños, etc."
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={!formData.warehouseId}
                >
                    Confirmar Devolución
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ClientEquipment = ({ clientId }) => {
    const [equipment, setEquipment] = useState([]);
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [openAssignDialog, setOpenAssignDialog] = useState(false);
    const [openReturnDialog, setOpenReturnDialog] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);

    useEffect(() => {
        if (clientId) {
            fetchData();
        }
    }, [clientId]);

    const fetchData = async () => {
        try {
            // This URL endpoint doesn't exist in your routes
            const equipmentResponse = await fetch(`/api/client-equipment/clients/${clientId}/equipment`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!equipmentResponse.ok) {
                throw new Error('Error al obtener equipos del cliente');
            }

            const equipmentData = await equipmentResponse.json();
            setEquipment(equipmentData);

            // Obtener productos de inventario con stock
            const itemsResponse = await fetch('/api/inventory/items', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!itemsResponse.ok) {
                throw new Error('Error al obtener productos de inventario');
            }

            const itemsData = await itemsResponse.json();

            // Obtener stock por almacén para cada producto
            const itemsWithStock = await Promise.all(itemsData.map(async (item) => {
                const stockResponse = await fetch(`/api/inventory/items/${item.id}/stock`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });

                if (stockResponse.ok) {
                    const stockData = await stockResponse.json();
                    return { ...item, stock: stockData };
                }

                return item;
            }));

            setItems(itemsWithStock);

            // Obtener almacenes
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!warehousesResponse.ok) {
                throw new Error('Error al obtener almacenes');
            }

            const warehousesData = await warehousesResponse.json();
            setWarehouses(warehousesData);

        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignEquipment = async (formData) => {
        try {
            // Convertir camelCase a snake_case
            const snakeCaseData = {
                item_id: formData.itemId,
                warehouse_id: formData.warehouseId,
                serial_number: formData.serialNumber,
                mac_address: formData.macAddress,
                notes: formData.notes
            };

            const response = await fetch(`/api/client-equipment/clients/${clientId}/equipment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(snakeCaseData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al asignar equipo');
            }

            setOpenAssignDialog(false);
            fetchData(); // Recargar datos
        } catch (error) {
            console.error('Error assigning equipment:', error);
            alert(error.message);
        }
    };

    const handleReturnEquipment = async (equipmentId, formData) => {
        try {
            const response = await fetch(`/api/client-equipment/clients/${clientId}/equipment/${equipmentId}/return`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al procesar la devolución');
            }

            setOpenReturnDialog(false);
            setSelectedEquipment(null);
            fetchData(); // Recargar datos
        } catch (error) {
            console.error('Error returning equipment:', error);
            alert(error.message);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Equipos Asignados</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAssignDialog(true)}
                >
                    Asignar Equipo
                </Button>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell>Detalles</TableCell>
                            <TableCell>Fecha Asignación</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {equipment.length > 0 ? (
                            equipment.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {item.item_name}
                                        </Typography>
                                        {item.model && (
                                            <Typography variant="caption" display="block">
                                                {item.brand} {item.model}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {item.serial_number && (
                                            <Typography variant="caption" display="block">
                                                <strong>S/N:</strong> {item.serial_number}
                                            </Typography>
                                        )}
                                        {item.mac_address && (
                                            <Typography variant="caption" display="block">
                                                <strong>MAC:</strong> {item.mac_address}
                                            </Typography>
                                        )}
                                        {item.notes && (
                                            <Typography variant="caption" display="block">
                                                <strong>Notas:</strong> {item.notes}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {item.assigned_at && format(new Date(item.assigned_at), 'dd/MM/yyyy')}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.status === 'active' ? 'Activo' : 'Devuelto'}
                                            color={item.status === 'active' ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {item.status === 'active' && (
                                            <Tooltip title="Devolver Equipo">
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedEquipment(item);
                                                        setOpenReturnDialog(true);
                                                    }}
                                                >
                                                    <ReturnIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {loading
                                        ? 'Cargando...'
                                        : 'No hay equipos asignados a este cliente'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Diálogos */}
            <AssignEquipmentDialog
                open={openAssignDialog}
                onClose={() => setOpenAssignDialog(false)}
                onSubmit={handleAssignEquipment}
                items={items}
                warehouses={warehouses}
            />

            {selectedEquipment && (
                <ReturnEquipmentDialog
                    open={openReturnDialog}
                    onClose={() => {
                        setOpenReturnDialog(false);
                        setSelectedEquipment(null);
                    }}
                    onSubmit={handleReturnEquipment}
                    equipment={selectedEquipment}
                    warehouses={warehouses}
                />
            )}
        </Box>
    );
};

export default ClientEquipment;